import React from 'react';
import Panel from '@views/Onboarding/panels/Panel';
import CopySection, { FinishStepButton } from '@views/Onboarding/panels/CopySection';
import styles from './PublicProfilePanel.module.scss';
import { SettingToggle } from '@views/Settings/components';
import cn from 'classnames';
import { URLInput } from '@components/FormInputs';
import { useAuthContext } from '@hooks/use-auth-context';
import Avatar from '@components/Avatar';
import faceCircleImg from '@app/img/onboarding/face-circle.png';
import faceDiamondImg from '@app/img/onboarding/face-diamond.png';
import faceSquareImg from '@app/img/onboarding/face-square.png';
import { usePublicProfileUrlEditor } from '@support/hooks/use-public-profile-url-editor';

export default function PublicProfilePanel({ proceedToNextStep }) {
  const {
    value,
    enabled,
    saving,
    isChanged,
    errorMsg,
    inputRef,
    handleInputChange,
    handleInputKeyDown,
    toggleEnabled,
    saveChanges,
  } = usePublicProfileUrlEditor({ forceDarkMode: true });

  const { user } = useAuthContext();

  const disableUrl = saving || !enabled;

  return (
    <Panel>
      <CopySection>
        <h2>Your Public Profile</h2>
        <p>Enable your public profile and share it with anyone inside and outside of Clay.</p>
        <p>
          <a href="https://go.clay.earth/edit-profile" target="_blank">
            Learn more.
          </a>
        </p>
        <FinishStepButton onClick={proceedToNextStep} disabled={saving}>
          Continue
        </FinishStepButton>
      </CopySection>
      <div className={cn(styles.wrap, !enabled && styles.disabled)}>
        <div className={styles.bg}>
          <img src={faceCircleImg} className={styles.faceCircle} />
          <img src={faceSquareImg} className={styles.faceSquare} />
        </div>
        <div className={cn(styles.glassBox, styles.avatarFrame)}>
          <Avatar className={styles.avatar} url={user?.avatar_url} size="lg" />
          <div className={styles.name}>
            {user?.first_name}&nbsp;{user?.last_name}
          </div>
          <div className={styles.memberNumber}>Member #{user?.id}</div>
        </div>
        <div className={cn(styles.glassBox, styles.form)}>
          <div className={styles.toggle}>
            <SettingToggle
              title="Enable My Public Profile"
              checked={enabled}
              disabled={saving}
              name="public_profile"
              onChange={toggleEnabled}
            />
          </div>
          <div className={styles.inputWrap}>
            <URLInput
              value={value}
              isChanged={isChanged}
              isSaving={saving}
              hasError={!!errorMsg}
              disabled={disableUrl}
              precedeText="clay.earth/profile/"
              precedeClassName={styles.precede}
              inputClassName={styles.input}
              inputRef={inputRef}
              onKeyDown={handleInputKeyDown}
              onChange={handleInputChange}
              onSaveClick={saveChanges}
              openLink
              urlBuilder={(input) => `https://clay.earth/profile/${input}`}
            />
          </div>
        </div>
        {!!errorMsg ? (
          <div className={cn(styles.subtext, styles.error)}>{errorMsg}</div>
        ) : (
          <div className={styles.subtext}>Set a custom URL for your public profile.</div>
        )}
        <img src={faceDiamondImg} className={styles.faceDiamond} />
      </div>
    </Panel>
  );
}
